/**
 * The player's options that are not key bindings, kept in the browser between visits: the groove in the visor's glass
 * (visor.ts), and whether a touch screen gets the on-screen controls or keyboard and mouse ones.
 */

/** Auto leaves it to the device (wantsTouchControls in touch.ts); on and off overrule it. */
export type TouchChoice = 'auto' | 'on' | 'off'

interface Preferences { visor: boolean; touch: TouchChoice }

const STORAGE_KEY = 'halo-ce.preferences'
const DEFAULTS: Preferences = { visor: true, touch: 'auto' }

let prefs: Preferences = { ...DEFAULTS }
try {
  const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<Preferences>
  if (typeof saved.visor === 'boolean') prefs.visor = saved.visor
  if (saved.touch === 'on' || saved.touch === 'off') prefs.touch = saved.touch
} catch { /* Defaults. */ }

export function preferences(): Readonly<Preferences> { return prefs }

export function setPreference<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
  prefs = { ...prefs, [key]: value }
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)) } catch { /* Private windows: the change lasts until the page closes. */ }
}

/**
 * A mouse or trackpad has been used on this page. An iPad with a Magic Keyboard still calls its touch screen the main
 * pointer, so this, and not the media queries alone, is what tells it to play with the keyboard and trackpad.
 */
export let mouseSeen = false
if (typeof addEventListener !== 'undefined') {
  const onPointer = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    mouseSeen = true
    removeEventListener('pointerdown', onPointer, true)
    removeEventListener('pointermove', onPointer, true)
  }
  addEventListener('pointerdown', onPointer, true)
  addEventListener('pointermove', onPointer, true)
}
